<script setup lang="ts">
import {
  formatRatioPercent,
  isZeroMoney,
  type Locale,
  type Money,
  moneyProportion,
  type Ratio,
  zeroMoney,
} from '@taler/domain'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import type { DonutSlice } from '../internal/view-types.ts'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ slices: readonly DonutSlice[]; total: Money; caption: string }>()
const { locale } = useI18n()
const currentLocale = computed(() => locale.value as Locale)

const RADIUS = 42
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
/** A hairline of canvas between two adjacent slices. */
const GAP = 1

const arcs = computed(() => {
  const zero = zeroMoney(props.total.currency)
  let offset = 0
  return props.slices.map((slice) => {
    const fraction = isZeroMoney(props.total) ? 0 : moneyProportion(slice.amount, zero, props.total)
    const length = Math.max(fraction * CIRCUMFERENCE - GAP, 0)
    const arc = {
      key: slice.key,
      dash: `${length} ${CIRCUMFERENCE - length}`,
      offset: -offset * CIRCUMFERENCE,
    }
    offset += fraction
    return arc
  })
})

function percentText(share: Ratio | undefined): string {
  if (share === undefined) return '—'
  return `${formatRatioPercent(share, { locale: currentLocale.value, minimumFractionDigits: 0, maximumFractionDigits: 0 })}%`
}
</script>

<template>
  <figure class="flex flex-wrap items-center gap-[var(--space-block)]" :aria-label="caption">
    <div class="relative h-44 w-44 shrink-0">
      <svg viewBox="0 0 112 112" aria-hidden="true" class="h-full w-full">
        <circle
          cx="56"
          cy="56"
          :r="RADIUS"
          fill="none"
          stroke="var(--color-surface-sunken)"
          stroke-width="16"
        />
        <circle
          v-for="(arc, index) in arcs"
          :key="arc.key"
          cx="56"
          cy="56"
          :r="RADIUS"
          fill="none"
          :stroke="slices[index]!.colour"
          stroke-width="16"
          :stroke-dasharray="arc.dash"
          :stroke-dashoffset="arc.offset"
          transform="rotate(-90 56 56)"
        />
      </svg>
      <div class="absolute inset-0 flex flex-col items-center justify-center">
        <MoneyAmount :value="total" size="medium" />
      </div>
    </div>
    <ul class="flex min-w-[15rem] flex-1 flex-col gap-[var(--space-inline)]">
      <li
        v-for="slice in slices"
        :key="slice.key"
        class="grid grid-cols-[0.75rem_minmax(0,1fr)_auto_3rem] items-center gap-[var(--space-inline)] text-[length:var(--text-step-2)]"
      >
        <span aria-hidden="true" class="h-3 w-3 rounded-[3px]" :style="{ background: slice.colour }" />
        <span class="truncate text-[color:var(--color-text)]">{{ slice.label }}</span>
        <MoneyAmount :value="slice.amount" size="medium" />
        <span class="text-right tabular-nums text-[color:var(--color-text-muted)]">{{
          percentText(slice.share)
        }}</span>
      </li>
    </ul>
  </figure>
</template>
